import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaFileAlt, FaSearch, FaDownload, FaEye, FaInfoCircle, FaTrashAlt, FaBuilding, FaCalendarAlt } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';
import DraggableReportModal from './DraggableReportModal';
import * as reportGenerator from '../../utils/reportGenerator';
import './Report.css';

const ReportList = () => {
  const { user } = useContext(AuthContext);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [organizationName, setOrganizationName] = useState('');
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [activeReportId, setActiveReportId] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateMessage, setRegenerateMessage] = useState('');

  // Function to fetch dashboard state (organization selection)
  const getDashboardState = () => {
    // First check sessionStorage for the most recent selection
    const currentSelectedOrgData = sessionStorage.getItem('currentSelectedOrganization');
    if (currentSelectedOrgData) {
      try {
        const currentSelectedOrg = JSON.parse(currentSelectedOrgData);
        setSelectedOrganization(currentSelectedOrg);
        setOrganizationName(currentSelectedOrg.name || '');
        return currentSelectedOrg;
      } catch (error) {
        console.error('Error parsing current selected organization:', error);
      }
    }
    
    // If not found in sessionStorage, check localStorage
    const dashboardState = localStorage.getItem('dashboardState');
    if (dashboardState) {
      try {
        const parsedState = JSON.parse(dashboardState);
        if (parsedState.selectedOrganization) {
          setSelectedOrganization(parsedState.selectedOrganization);
          setOrganizationName(parsedState.selectedOrganization.name || '');
          return parsedState.selectedOrganization;
        }
      } catch (error) {
        console.error('Error parsing dashboard state:', error);
      }
    }
    return null;
  };

  // Function to fetch reports
  const fetchReports = async (orgId = null) => {
    try {
      setLoading(true);
      setError('');
      let url = '/api/reports';
      
      // If we have an organization ID, use it to filter reports
      if (orgId) {
        url = `/api/reports?organizationId=${orgId}`;
        console.log(`Fetching reports for organization: ${orgId}`);
      }
      
      const res = await axios.get(url);
      console.log('Fetched reports:', res.data);
      
      // The backend now filters by organization ID, so we can directly use the response
      if (res.data && Array.isArray(res.data)) {
        console.log(`Received ${res.data.length} reports from the server`);
        setReports(res.data);
      } else {
        console.warn('Unexpected response format:', res.data);
        setReports([]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to load reports');
      setLoading(false);
    }
  };

  useEffect(() => {
    const org = getDashboardState();
    if (org && org._id) {
      fetchReports(org._id);
    } else {
      fetchReports();
    }
  }, [organizationName]); // Add organizationName as dependency

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilterType(e.target.value);
  };

  // Handle report download
  const handleDownload = async (reportId) => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      
      console.log('Downloading report:', reportId);
      
      // Get the report to determine if it's a client-side download
      const reportInfoResponse = await axios.get(`/api/reports/${reportId}`);
      const report = reportInfoResponse.data;
      
      if (!report) {
        throw new Error('Report not found');
      }
      
      // Check if this is a client-side download (Industry Analysis report)
      if (report.clientSideDownload) {
        // For Industry Analysis reports, we need to regenerate the PDF
        // since we don't store the actual file on the server
        setLoading(false);
        
        // Show a message to the user
        alert('This is an Industry Analysis report that was downloaded to your device when it was generated. Please go to the Industry Analysis page to regenerate this report if needed.');
        
        return;
      }
      
      // For server-side reports, proceed with normal download
      const response = await axios.get(`/api/reports/download/${reportId}`, {
        responseType: 'blob'
      });
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      
      // Set the filename based on report title
      const filename = `${report.title.replace(/\s+/g, '_')}.pdf`;
      link.setAttribute('download', filename);
      
      // Append to body, click, and clean up
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setLoading(false);
    } catch (error) {
      console.error('Error downloading report:', error);
      setError('Failed to download report: ' + (error.message || 'Unknown error'));
      setLoading(false);
      
      // As a last resort, try direct download
      try {
        window.open(`/api/reports/download/${reportId}`, '_blank');
      } catch (fallbackError) {
        console.error('Even fallback download failed:', fallbackError);
      }
    }
  };

  // Handle report deletion
  const handleDelete = async (reportId) => {
    if (window.confirm('Are you sure you want to delete this report?')) {
      try {
        setLoading(true);
        setError(''); // Clear any previous errors
        
        // Close modal if the report being deleted is currently viewed
        if (selectedReportId === reportId) {
          setSelectedReportId(null);
        }
        
        // Remove active highlight if this report was active
        if (activeReportId === reportId) {
          setActiveReportId(null);
        }
        
        console.log('Attempting to delete report:', reportId);
        
        // IMPORTANT: Always remove the report from the UI first (optimistic update)
        // This ensures a smooth user experience even if the server has issues
        setReports(prevReports => prevReports.filter(report => report._id !== reportId));
        
        // Then attempt the server deletion
        const response = await axios.delete(`/api/reports/${reportId}`);
        console.log('Report deleted successfully:', response.data);
        
        setLoading(false);
      } catch (error) {
        console.error('Error in delete operation:', error);
        
        // More detailed error handling
        let errorMessage = 'Failed to delete report';
        
        if (error.response) {
          // Server responded with an error status
          errorMessage = `Server error: ${error.response.data?.msg || error.response.statusText}`;
        } else if (error.request) {
          // Request was made but no response received
          errorMessage = 'No response from server. Please try again later.';
        } else {
          // Error in setting up the request
          errorMessage = `Error: ${error.message}`;
        }
        
        setError(errorMessage);
        setLoading(false);
      }
    }
  };
  
  // Handle opening the report modal
  const handleViewReport = (reportId) => {
    setSelectedReportId(reportId);
    setActiveReportId(reportId);
  };
  
  // Handle closing the report modal
  const handleCloseModal = () => {
    setSelectedReportId(null);
  };
  
  // Function to regenerate reports for an assessment
  const handleRegenerateReports = async () => {
    if (!selectedOrganization || !selectedOrganization._id) {
      setRegenerateMessage('Please select an organization first');
      return;
    }
    
    try {
      setRegenerating(true);
      setRegenerateMessage('Regenerating reports, please wait...');
      
      // Get assessments for the selected organization
      const assessmentsRes = await axios.get(`/api/assessments?organizationId=${selectedOrganization._id}`);
      const assessments = assessmentsRes.data || [];
      
      if (assessments.length === 0) {
        setRegenerateMessage('No assessments found for this organization');
        setRegenerating(false);
        return;
      }
      
      // Track regeneration results
      const results = [];
      
      // Process each assessment
      for (const assessment of assessments) {
        try {
          // Regenerate all report types for this assessment
          const regenerateRes = await axios.post(`/api/reports/regenerate/${assessment._id}`, {
            reportTypes: ['Scorecard', 'Scorecard Details']
          });
          
          results.push({
            assessmentTitle: assessment.title,
            success: regenerateRes.data.success,
            details: regenerateRes.data.results
          });
        } catch (assessmentError) {
          console.error(`Error regenerating reports for assessment ${assessment._id}:`, assessmentError);
          results.push({
            assessmentTitle: assessment.title,
            success: false,
            error: assessmentError.message
          });
        }
      }
      
      // Refresh the reports list
      await fetchReports(selectedOrganization._id);
      
      // Set success message
      const successCount = results.filter(r => r.success).length;
      setRegenerateMessage(`Successfully regenerated reports for ${successCount} out of ${assessments.length} assessments.`);
    } catch (error) {
      console.error('Error regenerating reports:', error);
      setRegenerateMessage(`Error: ${error.message || 'Unknown error'}`); 
    } finally {
      setRegenerating(false);
    }
  };

  // Add debugging for report filtering
  useEffect(() => {
    if (reports.length > 0) {
      console.log('All reports:', reports.map(r => ({ id: r._id, title: r.title, type: r.type })));
      console.log('Current filter type:', filterType);
    }
  }, [reports, filterType]);

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType ? report.type === filterType : true;
    
    // Debug filtering
    if (filterType && report.type !== filterType) {
      console.log(`Report ${report._id} (${report.title}) filtered out: type=${report.type}, filter=${filterType}`);
    }
    
    return matchesSearch && matchesFilter;
  });

  const renderReportCards = () => {
    if (loading) return <p>Loading reports...</p>;
    if (error) return <p>{error}</p>;
    if (filteredReports.length === 0) return <p>No reports found.</p>;

    return (
      <div className="report-grid">
        {filteredReports.map(report => (
          <div 
            key={report._id} 
            className={`card report-card ${activeReportId === report._id ? 'active-report' : ''}`}
          >
            <div className="report-card-header">
              <div className={`report-type-badge ${report.type?.toLowerCase().replace(/\s+/g, '-')}`} 
              style={{ 
                backgroundColor: report.type?.toLowerCase().includes('scorecard') ? 'rgba(36, 52, 68, 0.2)' : 'rgba(210, 186, 123, 0.2)',
                color: report.type?.toLowerCase().includes('scorecard') ? '#243444' : '#D2BA7B' 
              }}>
              {report.type || 'Report'}
            </div>
              <h3>{report.title}</h3>
            </div>
            
            <div className="report-card-body">
              <div className="report-meta">
                {report.assessmentId && report.assessmentId.title && (
                  <div className="report-assessment">
                    <FaFileAlt />
                    <span>{report.assessmentId.title}</span>
                  </div>
                )}
                
                <div className="report-date">
                  <FaCalendarAlt />
                  <span>{new Date(report.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
              
              {/* Show client-side download indicator for Industry Analysis reports */}
              {report.clientSideDownload && (
                <div className="client-download-indicator" style={{ 
                  backgroundColor: 'rgba(210, 186, 123, 0.1)', 
                  padding: '5px 10px',
                  borderRadius: '4px',
                  marginTop: '8px',
                  fontSize: '0.8rem',
                  color: '#D2BA7B',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <FaInfoCircle style={{ marginRight: '5px' }} />
                  <span>This report was downloaded to your device when generated</span>
                </div>
              )}
              
              <p className="report-preview">
                {report.description || 'No description available'}
              </p>
            </div>
            
            <div className="report-card-footer">
              {/* Download Button */}
              <button
                onClick={() => handleDownload(report._id)}
                title="Download Report"
                className="report-action-btn download-btn"
                style={{ color: '#243444' }}
              >
                <FaDownload size={18} />
              </button>
              
              {/* View Button */}
              <button
                onClick={() => handleViewReport(report._id)}
                title="View Report"
                className="report-action-btn view-btn"
                style={{ color: '#D2BA7B' }}
              >
                <FaEye size={18} />
              </button>
              
              {/* Delete Button */}
              <button
                onClick={() => handleDelete(report._id)}
                title="Delete Report"
                className="report-action-btn delete-btn"
                style={{ color: '#7C1227' }}
              >
                <FaTrashAlt size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="report-list-container">
      <div className="page-header">
        <div>
          <h1>Reports</h1>
          <p>View and download your AI-generated reports</p>
        </div>
      </div>
      
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="report-filters card">
        <div className="filter-row">
          <div className="search-box">
            <FaSearch />
            <input
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="filter-select">
            <label htmlFor="filter-type">Filter by Type:</label>
            <select
              id="filter-type"
              value={filterType}
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              <option value="Scorecard">Scorecard</option>
              <option value="Scorecard Details">Scorecard Details</option>
              <option value="Findings & Recommendations">Findings & Recommendations</option>
              <option value="Industry Comparison">Industry Comparison</option>
            </select>
          </div>
        </div>
        
        <div className="action-row" style={{ marginTop: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {regenerateMessage && (
            <div className="regenerate-message" style={{ 
              flex: 1, 
              color: regenerateMessage.includes('Error') ? '#7C1227' : regenerateMessage.includes('Success') ? '#243444' : '#4C5454'
            }}>
              {regenerateMessage}
            </div>
          )}
          
          <button 
            onClick={handleRegenerateReports}
            disabled={regenerating}
            className="btn" 
            style={{
              backgroundColor: '#243444',
              color: '#FFFAFA',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: regenerating ? 'not-allowed' : 'pointer',
              opacity: regenerating ? 0.7 : 1
            }}
          >
            {regenerating ? 'Regenerating...' : 'Regenerate Reports'}
          </button>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="empty-state card">
          <FaFileAlt className="empty-icon" />
          <h2>No Reports Found</h2>
          <p>You haven't generated any reports yet.</p>
          <p>Complete an assessment to generate AI-powered reports.</p>
          <Link to="/assessments" className="btn btn-primary">
            Go to Assessments
          </Link>
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="empty-state card">
          <FaSearch className="empty-icon" />
          <h2>No Matching Reports</h2>
          <p>No reports match your search criteria.</p>
          <button 
            onClick={() => {
              setSearchTerm('');
              setFilterType('');
            }} 
            className="btn btn-primary"
          >
            Clear Filters
          </button>
        </div>
      ) : (
        renderReportCards()
      )}

      {/* Draggable Report Modal */}
      {selectedReportId && (
        <DraggableReportModal 
          reportId={selectedReportId} 
          onClose={handleCloseModal} 
          onDownload={handleDownload}
        />
      )}
    </div>
  );
};

export default ReportList;
